// frontend/src/components/PaymentType/PaymentType.jsx
import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { 
  CreditCard, Save, X, Loader2, Building, AlertCircle, 
  User, Hash, Info, FileText, Plus, CheckCircle, Clock,
  DollarSign, Wallet, Smartphone, Landmark, Banknote,
  TrendingUp, TrendingDown, Layers, Package, ShoppingCart
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import "./PaymentType.css";

const url = process.env.REACT_APP_BACKEND_URL;

const PaymentType = () => {
  const { currentUser, userType, company } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [focused, setFocused] = useState({});
  
  // For system admins: company selection
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState("");
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  
  // Predefined payment types for quick selection
  const predefinedPaymentTypes = [
    {
      paymentstype: "Cash",
      description: "Physical currency notes and coins",
      icon: CreditCard,
      category: "cash"
    },
    {
      paymentstype: "Bank",
      description: "Bank transfers, wire transfers, and direct deposits",
      icon: Landmark,
      category: "bank"
    },
    {
      paymentstype: "Mobile Money",
      description: "Mobile money services like EVC Plus, eDahab, etc.",
      icon: Smartphone,
      category: "mobile"
    },
    {
      paymentstype: "Credit / Debit Card",
      description: "Payments made using Visa, Mastercard, debit cards, credit cards, prepaid cards, or POS terminals",
      icon: CreditCard,
      category: "card"
    }
  ];

  // Grouped by category
  const groupedTypes = {
    cash: predefinedPaymentTypes.filter(t => t.category === "cash"),
    bank: predefinedPaymentTypes.filter(t => t.category === "bank"),
    mobile: predefinedPaymentTypes.filter(t => t.category === "mobile"),
    card: predefinedPaymentTypes.filter(t => t.category === "card")
  };
  
  // Form data
  const [formData, setFormData] = useState({
    paymentstype: "",
    description: "",
    company_id: ""
  });

  // Calculate user permissions
  const isSystemAdminUser = () => {
    return userType === 'system' && (currentUser?.role === 'developer' || currentUser?.role === 'admin');
  };
  
  const isCompanyAdminUser = () => {
    return userType === 'company' && currentUser?.role === 'admin';
  };

  const userCanCreatePaymentType = () => {
    return isCompanyAdminUser() || isSystemAdminUser();
  };

  // Fetch companies for system admins
  const fetchCompanies = async () => {
    try {
      setLoadingCompanies(true);
      const response = await axios.get(`${url}/api/company`);
      
      if (response.data.success) {
        setCompanies(response.data.data || []);
      }
    } catch (error) {
      toast.error("Failed to load companies");
    } finally {
      setLoadingCompanies(false);
    }
  };

  // Check permissions and setup initial data
  useEffect(() => {
    const checkPermissionsAndSetup = async () => {
      try {
        if (!userCanCreatePaymentType()) {
          toast.error("You don't have permission to create payment types");
          navigate("/");
          return;
        }
        
        if (isSystemAdminUser()) {
          await fetchCompanies();
        } else if (userType === 'company' && company) {
          const companyId = company._id || company;
          setFormData(prev => ({
            ...prev,
            company_id: companyId
          }));
        }
        
        setLoading(false);
      } catch (error) {
        toast.error("Failed to load permissions");
        navigate("/");
      }
    };
    
    checkPermissionsAndSetup();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    setFormData(prev => ({ 
      ...prev, 
      [name]: value 
    }));
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const handleCompanyChange = (e) => {
    const value = e.target.value;
    setSelectedCompany(value);
    setFormData(prev => ({ ...prev, company_id: value }));
  };

  const handlePredefinedTypeSelect = (type) => {
    setFormData({
      paymentstype: type.paymentstype,
      description: type.description,
      company_id: formData.company_id
    });
    
    toast.info(`Selected: ${type.paymentstype}`);
  };

  const handleFocus = (field) => setFocused({ ...focused, [field]: true });
  const handleBlur = (field) => {
    setFocused({ 
      ...focused, 
      [field]: formData[field] && formData[field].length > 0 
    });
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.paymentstype.trim()) {
      newErrors.paymentstype = "Payment type name is required";
    } else if (formData.paymentstype.trim().length < 2) {
      newErrors.paymentstype = "Payment type name must be at least 2 characters";
    } else if (formData.paymentstype.trim().length > 100) {
      newErrors.paymentstype = "Payment type name cannot exceed 100 characters";
    }
    
    if (formData.description && formData.description.trim().length > 500) {
      newErrors.description = "Description cannot exceed 500 characters";
    }
    
    if (!formData.company_id) {
      newErrors.company_id = "Company selection is required";
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return false;
    }
    
    setErrors({});
    return true;
  };

  const handleCreateAllStandardTypes = async () => {
    if (
      isSystemAdminUser() &&
      !formData.company_id
    ) {
      toast.error("Please select a company first");
      return;
    }

    try {
      setBulkSubmitting(true);

      const payload = {
        paymentTypes: predefinedPaymentTypes.map((type) => ({
          paymentstype: type.paymentstype,
          description: type.description,
          status: "active",
        })),
      };

      if (isSystemAdminUser()) {
        payload.company_id = formData.company_id;
      }

      const response = await axios.post(
        `${url}/api/paymenttype/bulk`,
        payload,
      );

      if (response.data?.success) {
        const created =
          response.data?.data?.created || 0;

        const updated =
          response.data?.data?.updated || 0;

        toast.success(
          `Payment methods completed: ${created} created, ${updated} updated`,
        );

        window.setTimeout(() => {
          navigate("/ViewPaymentType");
        }, 1200);
      }
    } catch (error) {
      const responseData = error.response?.data;

      toast.error(
        responseData?.message ||
          "Failed to create standard payment methods",
      );
    } finally {
      setBulkSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error("Please fix form errors");
      return;
    }

    try {
      setSubmitting(true);
      const submitData = {
        ...formData,
        paymentstype: formData.paymentstype.trim()
      };

      const response = await axios.post(
        `${url}/api/paymenttype`,
        submitData,
      );

      if (response.data.success) {
        toast.success(response.data.message || "Payment type created successfully!");
        
        // Reset form
        setFormData({ 
          paymentstype: "",
          description: "",
          company_id: userType === 'company' && company ? (company._id || company) : ""
        });
        setSelectedCompany("");
        
        toast.info(
          "You can select another payment method or enter a custom one.",
        );
      } else {
        toast.error(response.data.message || "Failed to create payment type");
      }
    } catch (error) {
      const errorMsg = error.response?.data?.message || "Failed to create payment type";
      toast.error(errorMsg);
      
      if (error.response?.data?.errors) {
        setErrors(prev => ({
          ...prev,
          ...Object.fromEntries(error.response.data.errors.map(err => [err.field, err.message]))
        }));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate("/ViewPaymentType");
  };

  if (loading) {
    return (
      <div className="form-wrapper">
        <div className="paymenttype-form-container">
          <div className="loading-container">
            <Loader2 className="animate-spin" size={32} />
            <p>Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="form-wrapper">
      <div className="paymenttype-form-container">
        <button
          className="close-btn"
          onClick={handleCancel}
          title="Close"
        >
          <X size={20} />
        </button>

        <div className="form-header">
          <CreditCard size={24} />
          <h2 className="form-title">Add New Payment Type</h2>
          <p className="form-subtitle">
            {isSystemAdminUser() 
              ? "Create a payment type for any company"
              : `Create a payment type for ${company?.company_name || 'your company'}`
            }
          </p>
        </div>

        <div
          className="role-indicator"
          style={{
            background: 'darkblue',
            color: 'white',
            padding: '10px 20px',
            borderRadius: '8px',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}
        >
          <Building size={20} />
          <div>
            <strong>
              {isSystemAdminUser() ? "System Administrator" : "Company Admin"}
            </strong>
            <div style={{ fontSize: '12px', opacity: 0.9 }}>
              {isSystemAdminUser()
                ? "You can create payment types for any company"
                : `You can only create payment types for ${company?.company_name || 'your company'}`
              }
            </div>
          </div>
        </div>

        {/* Quick Select Container */}
        <div className="quick-select-container">
          <h4 className="quick-select-title">
            <Package size={16} className="mr-2" />
            Quick Select Standard Payment Types
          </h4>
          <p className="quick-select-subtitle">
            Click a standard payment method to auto-fill the form,
            or create all four standard methods in one operation.
          </p>

          <div className="standard-payment-bulk-panel">
            <div>
              <h4>Create All Standard Payment Methods</h4>
              <p>
                Creates Cash, Bank, Mobile Money, and
                Credit / Debit Card for the selected company.
              </p>
            </div>

            <button
              type="button"
              className="create-all-payment-btn"
              onClick={handleCreateAllStandardTypes}
              disabled={
                bulkSubmitting ||
                submitting ||
                (isSystemAdminUser() &&
                  !formData.company_id)
              }
            >
              {bulkSubmitting ? (
                <>
                  <Loader2
                    className="animate-spin"
                    size={17}
                  />
                  Creating All...
                </>
              ) : (
                <>
                  <Plus size={17} />
                  Create All 4 Methods
                </>
              )}
            </button>
          </div>
          
          {/* Cash Payment Types */}
          <div className="category-section">
            <h5 className="category-title">
              <CreditCard size={14} /> Cash Payments
            </h5>
            <div className="predefined-types-grid">
              {groupedTypes.cash.map((type, index) => {
                const Icon = type.icon;
                return (
                  <button
                    key={`cash-${index}`}
                    type="button"
                    className="predefined-type-card cash"
                    onClick={() => handlePredefinedTypeSelect(type)}
                    title={`Click to select: ${type.description}`}
                  >
                    <div className="type-card-header">
                      <Icon size={18} />
                      <div className="type-name">{type.paymentstype}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Bank Payment Types */}
          <div className="category-section">
            <h5 className="category-title">
              <Landmark size={14} /> Bank Payments
            </h5>
            <div className="predefined-types-grid">
              {groupedTypes.bank.map((type, index) => {
                const Icon = type.icon;
                return (
                  <button
                    key={`bank-${index}`}
                    type="button"
                    className="predefined-type-card bank"
                    onClick={() => handlePredefinedTypeSelect(type)}
                    title={`Click to select: ${type.description}`}
                  >
                    <div className="type-card-header">
                      <Icon size={18} />
                      <div className="type-name">{type.paymentstype}</div>
                    </div>
                    <div className="type-notes">{type.description}</div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Mobile Money Payment Types */}
          <div className="category-section">
            <h5 className="category-title">
              <Smartphone size={14} /> Mobile Money
            </h5>
            <div className="predefined-types-grid">
              {groupedTypes.mobile.map((type, index) => {
                const Icon = type.icon;
                return (
                  <button
                    key={`mobile-${index}`}
                    type="button"
                    className="predefined-type-card mobile"
                    onClick={() => handlePredefinedTypeSelect(type)}
                    title={`Click to select: ${type.description}`}
                  >
                    <div className="type-card-header">
                      <Icon size={18} />
                      <div className="type-name">{type.paymentstype}</div>
                    </div>
                    <div className="type-notes">{type.description}</div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Card Payment Types */}
          <div className="category-section">
            <h5 className="category-title">
              <CreditCard size={14} /> Card Payments
            </h5>
            <div className="predefined-types-grid">
              {groupedTypes.card.map((type, index) => {
                const Icon = type.icon;
                return (
                  <button
                    key={`card-${index}`}
                    type="button"
                    className="predefined-type-card card"
                    onClick={() => handlePredefinedTypeSelect(type)}
                    title={`Click to select: ${type.description}`}
                  >
                    <div className="type-card-header">
                      <Icon size={18} />
                      <div className="type-name">{type.paymentstype}</div>
                    </div>
                    <div className="type-notes">{type.description}</div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <form className="paymenttype-form" onSubmit={handleSubmit}>
          <div className="form-grid">
            {/* Company Selection (System Admins Only) */}
            {isSystemAdminUser() && (
              <div className={`form-field required ${focused.company_id || formData.company_id ? "filled" : ""} ${errors.company_id ? 'error' : ''}`}>
                <div className="select-wrapper">
                  <select
                    name="company_id"
                    value={selectedCompany}
                    onChange={handleCompanyChange}
                    onFocus={() => handleFocus("company_id")}
                    onBlur={() => handleBlur("company_id")}
                    required
                    disabled={loadingCompanies}
                  >
                    <option value="">Select Company</option>
                    {companies.map((comp) => (
                      <option key={comp._id} value={comp._id}>
                        {comp.company_name} ({comp.company_code})
                      </option>
                    ))}
                  </select>
                  <Building size={16} className="select-icon" />
                </div>
                <label>Company *</label>
                {errors.company_id && <small className="field-error">{errors.company_id}</small>}
                {loadingCompanies && <small className="field-hint">Loading companies...</small>}
              </div>
            )}

            {/* Payment Type Name */}
            <div className={`form-field required ${focused.paymentstype || formData.paymentstype ? "filled" : ""} ${errors.paymentstype ? 'error' : ''}`}>
              <input
                type="text"
                name="paymentstype"
                value={formData.paymentstype}
                onChange={handleChange}
                onFocus={() => handleFocus("paymentstype")}
                onBlur={() => handleBlur("paymentstype")}
                placeholder="Enter payment type name"
                required
                maxLength={100}
              />
              <label>
                <CreditCard size={14} className="mr-2" />
                Payment Type Name *
              </label>
              {errors.paymentstype && <small className="field-error">{errors.paymentstype}</small>}
            </div>

            {/* Description */}
            <div className={`form-field ${focused.description || formData.description ? "filled" : ""} ${errors.description ? 'error' : ''}`}>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                onFocus={() => handleFocus("description")}
                onBlur={() => handleBlur("description")}
                placeholder="Enter description (optional)"
                rows="3"
                maxLength={500}
              />
              <label>
                <FileText size={14} className="mr-2" />
                Description
              </label>
              {errors.description && <small className="field-error">{errors.description}</small>}
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="cancel-btn"
              onClick={handleCancel}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="submit-btn"
              disabled={submitting || bulkSubmitting}
            >
              {submitting ? (
                <>
                  <Loader2 className="animate-spin mr-2" size={16} />
                  Creating...
                </>
              ) : (
                <>
                  Create Payment Type
                  <Save className="ml-2" size={16} />
                </>
              )}
            </button>
          </div>
        </form>
        <style>{`
          .standard-payment-bulk-panel {
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: 20px;
            padding: 18px 20px;
            margin: 18px 0 24px;
            border: 1px solid #dfe3ef;
            border-left: 4px solid #f99933;
            border-radius: 10px;
            background: #ffffff;
          }

          .standard-payment-bulk-panel h4 {
            margin: 0 0 6px;
            color: #170f48;
          }

          .standard-payment-bulk-panel p {
            margin: 0;
            color: #697080;
            font-size: 14px;
            line-height: 1.5;
          }

          .create-all-payment-btn {
            border: none;
            border-radius: 8px;
            padding: 12px 18px;
            background: #170f48;
            color: #ffffff;
            font-weight: 700;
            cursor: pointer;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
            white-space: nowrap;
            min-width: 205px;
          }

          .create-all-payment-btn:hover:not(:disabled) {
            background: #29216d;
          }

          .create-all-payment-btn:disabled {
            opacity: 0.6;
            cursor: not-allowed;
          }

          @media (max-width: 768px) {
            .standard-payment-bulk-panel {
              flex-direction: column;
              align-items: stretch;
            }

            .create-all-payment-btn {
              width: 100%;
            }
          }
        `}</style>
      </div>
    </div>
  );
};

export default PaymentType;